/**
 * The MintUpcoming container
 */
import { Medium } from "@bkry/bowline-components/Medium";
import { Roadmap } from "@bkry/bowline-components/Roadmap";
import {
  TrunkatedAddress,
  ScrollToTop as useScrollToTop,
} from "@bkry/bowline-components/Utils";
import { chains } from "@bkry/bowline-connectors";
import { genericAbiRead } from "@bkry/bowline-redux/contracts";
import {
  getMintTierAttributes,
  listTiersForProject,
} from "@bkry/bowline-redux/mintTiers";
import { listTokens } from "@bkry/bowline-redux/tokens";
import { env } from "@bkry/bowline-utils";
import { useWeb3React } from "@web3-react/core";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import ReactPlayer from "react-player";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import Faq from "../components/Faq";
import Gen3ratorPartnerPrices from "../components/Gen3rator/Gen3ratorPartnerPrices";
import ImageContentSplit from "../components/ImageContentSplit";
import { UserLootboxes } from "../components/Lootboxes";
import MintButtons from "../components/Minting/MintPassCard/utils/MintButtons";
import MintingWrapper from "../components/Minting/MintingWrapper";
import Page from "../components/Page/Page-component";

const { REACT_APP_CDN_PATH, REACT_APP_PROJECT_ID } = env;

const {
  REACT_APP_MINT_TIER_GEN3RATOR_ID,
  REACT_APP_MINT_TIER_GEN3CHIP_ID,
  REACT_APP_MINT_TIER_GEN3RATOR_AND_GEN3CHIP_ID,
  REACT_APP_GENERATOR_CONTRACT_ID,
  REACT_APP_GEN3_CHIP_CONTRACT_ID,
  REACT_APP_GENERATOR_MINIO_ENDPOINT,
} = typeof window !== "undefined" && window.env ? window.env : process.env;

/**
 * Lootbox container, the landing page for logged in users
 */
const Lootbox = (props, context) => {
  useScrollToTop();
  const dispatch = useDispatch();
  const { account, provider, connector, chainId } = useWeb3React();
  // const networkName =
  //   provider?.network?.name === "matic" ? "polygon" : provider?.network?.name;

  useEffect(() => {
    dispatch(
      listTiersForProject({
        projectId: REACT_APP_PROJECT_ID,
        address: account,
      })
    );
    window.scrollTo(0, 0);
  }, [dispatch, REACT_APP_PROJECT_ID, account]);

  const getTokens = () => {
    dispatch(
      listTokens({
        projectId: REACT_APP_PROJECT_ID,
        account: account,
        sorting: {},
        filters: {
          wallet_address: account,
          contract_id: REACT_APP_GENERATOR_CONTRACT_ID,
        },
        pageNum: 1,
      })
    );
    dispatch(
      listTokens({
        projectId: REACT_APP_PROJECT_ID,
        account: account,
        sorting: {},
        filters: {
          wallet_address: account,
          contract_id: REACT_APP_GEN3_CHIP_CONTRACT_ID,
        },
        pageNum: 1,
      })
    );
  };

  const [voucherCode, setVoucherCode] = useState("");
  const [showVoucherCodeHint, setShowVoucherCodeHint] = useState(false);
  const [mintTierId, setMintTierId] = useState(null);
  const [mintTierTokenTitle, setMintTierTokenTitle] = useState(null);
  const [hideGenericModals, setHideGenericModals] = useState(false);

  const generatorMintTier = useSelector((state) =>
    getMintTierAttributes(state, REACT_APP_MINT_TIER_GEN3RATOR_ID)
  );

  const chipMintTier = useSelector((state) =>
    getMintTierAttributes(state, REACT_APP_MINT_TIER_GEN3CHIP_ID)
  );

  const mintTier = useSelector((state) =>
    getMintTierAttributes(state, mintTierId)
  );

  const mintSuccess = useSelector((state) => state?.contracts?.mintSuccess);

  const [alreadyMintedAmountGenerator, setAlreadyMintedAmountGenerator] =
    useState(0);
  const [alreadyMintedAmountChip, setAlreadyMintedAmountChip] = useState(0);

  const networkToChainId = {
    ethereum: 1,
    ropsten: 3,
    rinkeby: 4,
    goerli: 5,
    sepolia: 11155111,
    polygon: 137,
    mumbai: 80001,
    lukso: 42,
    lukso_testnet: 4201,
    amoy: 80002,
  };

  const networkToChainHex = {
    ethereum: "0x1",
    ropsten: "0x3",
    rinkeby: "0x4",
    goerli: "0x5",
    sepolia: "0xAA36A7",
    polygon: "0x89",
    mumbai: "0x13881",
    lukso: "0x2A",
    lukso_testnet: "0x1069",
    amoy: "0x13882",
  };

  const suggestNetworkChange =
    account && networkToChainId[generatorMintTier?.network] !== chainId;

  const handleNetworkChange = async (chainHex, chainId) => {
    try {
      let chain = await connector?.provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: chainHex }],
      });
      console.log("chain", chain);
    } catch (switchError) {
      console.log("error", switchError);
      if (switchError.code === 4902) {
        const addChainParameters = chains.getAddChainParameters(chainId);
        console.log("addChainParameters", addChainParameters);
        const rpcUrls =
          addChainParameters?.rpcUrls?.length > 1
            ? addChainParameters?.rpcUrls.filter(
                (rpcUrl) => !rpcUrl.includes("infura")
              )
            : addChainParameters?.rpcUrls;
        try {
          await connector?.provider.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                rpcUrls,
                nativeCurrency: addChainParameters.nativeCurrency,
                chainName: addChainParameters.chainName,
                chainId: chainHex,
              },
            ],
          });
        } catch (addError) {
          console.log("error", addError);
        }
      }
    }
  };

  const getAlreadyMintedAmountGenerator = async () => {
    if (suggestNetworkChange) return false;
    if (!generatorMintTier?.already_minted_amount_abi?.name) return true;
    // if (networkName !== generatorMintTier?.network) return;
    dispatch(
      genericAbiRead({
        functionAbi: generatorMintTier?.already_minted_amount_abi,
        contractAddress: generatorMintTier?.contract_address,
        account: account,
        provider: provider,
        genericInputs: {
          key: account,
        },
        successCallbackFnc: (data) => {
          setAlreadyMintedAmountGenerator(
            parseInt(data?._isBigNumber ? data.toString() : data)
          );
        },
        errorCallbackFnc: (data) => {
          console.log("already minted generator error", data);
        },
      })
    );
  };

  // get already minted amount every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      getAlreadyMintedAmountGenerator();
      getAlreadyMintedAmountChip();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (mintSuccess) {
      getTokens();
      getAlreadyMintedAmountGenerator();
      getAlreadyMintedAmountChip();
    }
  }, [mintSuccess]);

  const getAlreadyMintedAmountChip = async () => {
    if (suggestNetworkChange) return false;
    if (!chipMintTier?.already_minted_amount_abi?.name) return true;
    // if (networkName !== chipMintTier?.network) return;
    dispatch(
      genericAbiRead({
        functionAbi: chipMintTier?.already_minted_amount_abi,
        contractAddress: chipMintTier?.contract_address,
        account: account,
        provider: provider,
        genericInputs: {
          key: account,
        },
        successCallbackFnc: (data) => {
          setAlreadyMintedAmountChip(
            parseInt(data?._isBigNumber ? data.toString() : data)
          );
        },
        errorCallbackFnc: (data) => {
          console.log("already minted chip error", data);
        },
      })
    );
  };

  useEffect(() => {
    if (generatorMintTier && chipMintTier && !suggestNetworkChange) {
      getAlreadyMintedAmountGenerator();
      getAlreadyMintedAmountChip();
    }
  }, [
    provider,
    account,
    chipMintTier,
    generatorMintTier,
    suggestNetworkChange,
  ]);

  const updateVoucherCode = (value) => {
    setVoucherCode(value);
    if (value.toLowerCase().startsWith("gen3-g")) {
      setMintTierId(REACT_APP_MINT_TIER_GEN3RATOR_ID);
      setMintTierTokenTitle("GEN3RATOR");
    } else if (value.toLowerCase().startsWith("gen3-c")) {
      setMintTierId(REACT_APP_MINT_TIER_GEN3CHIP_ID);
      setMintTierTokenTitle("GEN3CHIP");
    } else if (value.toLowerCase().startsWith("gen3-b")) {
      setMintTierId(REACT_APP_MINT_TIER_GEN3RATOR_AND_GEN3CHIP_ID);
      setMintTierTokenTitle("GEN3RATOR and GEN3CHIP bundle");
    } else {
      setMintTierId(null);
      setMintTierTokenTitle(null);
    }
  };

  const renderMintTierContent = () => {
    if (mintTier?.public_medium_original && mintTier?.public_medium_metadata) {
      return (
        <div>
          <Medium
            mediumData={{
              ...mintTier?.public_medium_original,
              ...mintTier?.public_medium_metadata,
            }}
            className="justify-content-center mb-3"
            altTitle={mintTier?.title}
            skipLazyLoading
            autoPlay
            loop
          />
        </div>
      );
    } else if (mintTierId === REACT_APP_MINT_TIER_GEN3RATOR_ID) {
      return (
        <div>
          <img
            src={
              (REACT_APP_CDN_PATH || "") +
              "/gen3rator/500x500_GEN3RATOR_PREVIEW_Without_Chip.png"
            }
            className="w-100 img-fluid"
          />
        </div>
      );
    } else if (mintTierId === REACT_APP_MINT_TIER_GEN3CHIP_ID) {
      return (
        <div>
          <img
            src={
              "https://metadata.bowline.app/0xsamsunggen3rator/assets/chip/chip.png"
            }
            className="w-100 img-fluid"
          />
        </div>
      );
    } else if (mintTierId === REACT_APP_MINT_TIER_GEN3RATOR_AND_GEN3CHIP_ID) {
      return (
        <div>
          <img
            src={
              (REACT_APP_CDN_PATH || "") +
              "/images/gen3rator/gen3rator-with-chip.png"
            }
            className="w-100 img-fluid"
          />
        </div>
      );
    } else {
      return (
        <div className="overflow-hidden d-flex d-md-block justify-content-center">
          <ReactPlayer
            url={(REACT_APP_CDN_PATH || "") + "/generator_teaser.mp4"}
            controls={false}
            playing={true}
            playsinline
            muted={true}
            loop={true}
            width="140%"
            height="auto"
          />
        </div>
      );
    }
  };

  const successVideoUrl = `${REACT_APP_GENERATOR_MINIO_ENDPOINT}/minting.mp4`;

  return (
    <Page id="home" title="Samsung GEN3RATOR" className="mb-3">
      <MintingWrapper
        mintTierId={mintTierId}
        tokenTitle={mintTierTokenTitle}
        hideGenericModals={hideGenericModals}
        successVideoUrl={successVideoUrl}
      >
        <Row className="gy-5 bigger-margin-bottom">
          <Col xs={12} md={6} className="justify-content-center pe-lg-5">
            {renderMintTierContent()}
          </Col>
          <Col
            className="d-flex flex-column minting-wrapper ps-lg-5"
            xs={12}
            md={6}
          >
            <h2 className="text-uppercase fw-bold mb-3">
              {context.t("generators.header.title")}
            </h2>
            <p className="mb-5">{context.t("generators.header.description")}</p>
            {mintTier?.description && (
              <div
                className="mb-5"
                dangerouslySetInnerHTML={{ __html: mintTier?.description }}
              />
            )}
            <div className="mt-3 border-top border-bottom py-3">
              <Row>
                <Col md={6}>
                  <div className="mb-3">GEN3RATORs</div>
                  <div className="d-flex">
                    <div className="w-50 d-flex flex-column justify-content-between me-3">
                      <small className="text-muted text-uppercase mb-3">
                        {context.t("Total Minted")}
                      </small>
                      <p className="fw-bold">
                        {alreadyMintedAmountGenerator ||
                          generatorMintTier?.contract_total_supply ||
                          0}
                      </p>
                    </div>
                    <div className="w-50 d-flex flex-column justify-content-between me-3">
                      <small className="text-muted text-uppercase mb-3">
                        {context.t("Contract")}
                      </small>
                      <p className="fw-bold">
                        {generatorMintTier?.contract_address && (
                          <a
                            className="text-decoration-none small border rounded-pill py-1 px-2"
                            style={{ width: "120px" }}
                            href={`https://opensea.io/assets/matic/${generatorMintTier?.contract_address}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <TrunkatedAddress
                              address={generatorMintTier?.contract_address}
                            />
                          </a>
                        )}
                      </p>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">GEN3 Chips</div>
                  <div className="d-flex">
                    <div className="w-50 d-flex flex-column justify-content-between me-3">
                      <small className="text-muted text-uppercase mb-3">
                        {context.t("Total Minted")}
                      </small>
                      <p className="fw-bold">
                        {alreadyMintedAmountChip ||
                          chipMintTier?.contract_total_supply ||
                          0}
                      </p>
                    </div>
                    <div className="w-50 d-flex flex-column justify-content-between me-3">
                      <small className="text-muted text-uppercase mb-3">
                        {context.t("Contract")}
                      </small>
                      <p className="fw-bold">
                        {chipMintTier?.contract_address && (
                          <a
                            className="text-decoration-none small border rounded-pill py-1 px-2"
                            style={{ width: "120px" }}
                            href={`https://opensea.io/assets/matic/${chipMintTier?.contract_address}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <TrunkatedAddress
                              address={chipMintTier?.contract_address}
                            />
                          </a>
                        )}
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            <div className="mb-3 mt-5">
              <Label className="p-0 bold">
                {context.t("Please enter your unique Mint Voucher")}*
              </Label>
              <Input
                placeholder={context.t("e.g. CODE123 (required)")}
                defaultValue={voucherCode}
                onChange={(evt) => {
                  updateVoucherCode(evt.target.value);
                }}
              />
            </div>
            <span
              onClick={() => setShowVoucherCodeHint(true)}
              className="small text-info pointer-url"
            >
              {context.t("You don't have a code yet? Find out more here.")}
            </span>
            <Modal
              isOpen={showVoucherCodeHint}
              toggle={() => setShowVoucherCodeHint(false)}
            >
              <ModalHeader
                close={
                  <button
                    onClick={() => setShowVoucherCodeHint(false)}
                    className="btn-close btn-close-white"
                  />
                }
              >
                <>{context.t("generators.nocodemodal.header")}</>
              </ModalHeader>
              <ModalBody>
                <p
                  dangerouslySetInnerHTML={{
                    __html: context.t("generators.nocodemodal.body"),
                  }}
                />
              </ModalBody>
            </Modal>
            {mintTierId ? (
              <MintButtons
                mintTierId={mintTierId}
                outsideVoucherCode={voucherCode}
              />
            ) : (
              <Row>
                <div className="my-3 d-grid gap-2 col-sm-12 col-md-4 mx-auto">
                  <Button color="primary" disabled={true} size="lg">
                    {context.t("Mint")}
                  </Button>
                </div>
              </Row>
            )}
          </Col>
        </Row>
      </MintingWrapper>
      {suggestNetworkChange && (
        <Container>
          <Alert color="dark" className="text-center">
            <p className="small">
              {context.t(
                "You are currently connected to a different network than this contract is deployed on. Please change your network to {networkName} in your wallet.",
                { networkName: generatorMintTier?.network }
              )}
            </p>
            <div className="d-flex">
              <Button
                className="mx-auto"
                color="primary"
                onClick={() =>
                  handleNetworkChange(
                    networkToChainHex[generatorMintTier?.network],
                    networkToChainId[generatorMintTier?.network]
                  )
                }
              >
                {context.t("Switch to {networkName} network", {
                  networkName: generatorMintTier?.network,
                })}
              </Button>
            </div>
          </Alert>
        </Container>
      )}
      <UserLootboxes
        setHideGenericModals={setHideGenericModals}
        useMetadata={suggestNetworkChange}
        getTokens={getTokens}
      />
      <div className="bigger-margin-top bigger-margin-bottom">
        <Roadmap roadmapSlug="gen3rator" />
        <Gen3ratorPartnerPrices />
        <Container>
          <ImageContentSplit
            title={context.t("generators.howto.title")}
            content={context.t("generators.howto.copy")}
            imageUrl={
              (REACT_APP_CDN_PATH || "") +
              "/images/gen3rator/gen3rator-with-chip.png"
            }
          />
        </Container>
        <Container>
          <Faq faqListSlugs={["gen3rator", "minting"]} />
        </Container>
      </div>
    </Page>
  );
};

/**  define proptype for the 'translation' function  */
Lootbox.contextTypes = {
  t: PropTypes.func,
};

Lootbox.propTypes = {};

/** export the component as redux connected component */
export default Lootbox;
